// Build identity, and the probe behind the "new version available" prompt.
//
// A single-page app never re-fetches its own entry document while a tab stays
// open, so a tab left open across a deploy keeps running the bundle it booted
// with. Nothing forces it forward either: the deploy uploads with `s3 sync`
// WITHOUT `--delete` (deliberately — mid-session tabs must keep resolving the
// chunks they were built against), so every previously deployed hashed chunk
// stays reachable, nothing 404s, and the app never breaks. It just quietly
// renders old copy over current data, indefinitely. Observed 2026-08-13: a
// three-week-old bundle showing metrics wording that had been deleted from the
// codebase, which a reader then made decisions from.
//
// The build stamps its commit sha into `__BUILD_ID__` and writes the same value
// to `dist/version.json` (see vite.config.ts). Polling that file and comparing
// is the only way an open tab can learn a newer build is live.
//
// Every failure path below returns null — "no opinion". A missing, unreachable,
// or malformed manifest must leave the dashboard behaving exactly as it did
// before this module existed; a broken version check is never worth degrading
// the dashboard over.

/** Commit sha of the running bundle. Empty for local builds (see vite.config.ts). */
export const BUILD_ID: string = __BUILD_ID__;

/** Root-relative: the SPA is always served from the domain root. */
const VERSION_MANIFEST_PATH = '/version.json';

/**
 * Read the build id that is currently deployed, or null when it cannot be
 * established for any reason.
 *
 * The request carries its own freshness and depends on no response header to
 * get it: a unique URL per call, which nothing keyed on URL — browser cache or
 * edge — can answer from store, plus `cache: 'no-store'`, which both bypasses
 * the browser's own cache and makes it send `Cache-Control: no-cache` upstream.
 * `dashboard-static` serving this file `no-store` is defence in depth on top of
 * that, and deliberately only that: the worker ships on its own release train,
 * so this client had to be correct before it, not because of it. Being pinned
 * to the first answer a tab ever got is precisely the failure this module
 * exists to end.
 */
export const fetchDeployedBuildId = async (
  signal?: AbortSignal,
): Promise<string | null> => {
  try {
    const response = await fetch(`${VERSION_MANIFEST_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
      signal,
    });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) return null;
    const { version } = body as { version?: unknown };
    // An empty id is what a local build writes — indistinguishable from "not
    // stamped", so treat it as no answer rather than as a difference.
    return typeof version === 'string' && version !== '' ? version : null;
  } catch {
    // Offline, aborted, 5xx-with-HTML-body, malformed JSON — all the same
    // answer: say nothing, try again on the next poll.
    return null;
  }
};
